import {
  POST_TYPES,
  kindsForPostTypes,
  matchesPostTypes,
  sortEvents,
  type PostType,
} from "@/nostr/api/feed"
import type { NostrEvent } from "@/nostr/types"
import { shortNpub } from "@/utils/nip19"
import { nowSeconds } from "@/utils/time"
import { simplifyUrl } from "@/utils/url"
import {
  AUTHOR_MODES,
  FOLLOWING_MODES,
  matchesAuthors,
  matchesFollowing,
  relayAuthorsFor,
  uniquePubkeys,
  type AuthorMode,
  type FollowingMode,
} from "./authorFilter"
import {
  engagementOf,
  hasEngagementThreshold,
  matchesEngagement,
  type EngagementIndex,
} from "./engagementFilter"
import { LANGUAGE_OPTIONS, languageLabel, matchesLanguageFilter } from "./languageFilter"
import { MEDIA_FILTERS, matchesMediaFilters, type MediaFilter } from "./mediaFilter"
import { TIME_RANGES, matchesTimeRange, timeBounds, type CustomRange, type TimeRange } from "./timeFilter"
import { TOPIC_MODES, matchesTopicFilter, type TopicMode } from "./topicFilter"
import { TOPICS, TOPIC_LABELS, isTopicId, type TopicId } from "./topicLexicon"

/**
 * The unified filtering engine.
 *
 * One serialisable state object describes every filter, and one function applies
 * it to a list of events. Pages only own the state (through `usePostFilters`)
 * and call `applyPostFilters`, so the rules can never drift between the feed,
 * the relay page, search, bookmarks and the profiles.
 *
 * Division of labour:
 *
 * - **relay side** (`relayFilterHints`): post types -> `kinds`, a time range ->
 *   `since`/`until`, an author *include* list -> `authors`;
 * - **client side** (`filterPosts`): notes vs replies, media, language, muted
 *   words, engagement thresholds, author exclusion and following status, because
 *   NIP-01 cannot express them.
 */
export const SORT_MODES = ["latest", "oldest", "likes", "reposts", "replies", "engagement"] as const

export type SortMode = (typeof SORT_MODES)[number]

export const FILTER_CATEGORIES = ["content", "time", "people"] as const

export type FilterCategory = (typeof FILTER_CATEGORIES)[number]

export interface PostFilterState {
  /** Empty means "all types". Notes and replies share kind 1 (client side split). */
  postTypes: PostType[]
  /** Empty means "all media". Several values match on the union. */
  media: MediaFilter[]
  /** Selected topics; several values match on the union. Empty means all topics. */
  topics: TopicId[]
  /** Whether the selected topics are kept or hidden. */
  topicMode: TopicMode
  /** `"all"`, `"detected"` or a BCP-47 primary subtag. */
  language: string
  /** Applies the muted words configured in Settings. */
  hideMutedWords: boolean
  timeRange: TimeRange
  customRange: CustomRange
  minLikes: number
  minReposts: number
  minReplies: number
  minTotalEngagement: number
  sort: SortMode
  authors: string[]
  authorMode: AuthorMode
  following: FollowingMode
  /** Selected source relays; empty means "every relay of this list". */
  relays: string[]
}

/** Values the engine needs that are not part of the filter selection. */
export interface FilterContext {
  /** Muted words from Settings. Applied only when `hideMutedWords` is on. */
  mutedWords?: string[]
  /** The reader's kind 3 follow list, for the following modes. */
  followingSet?: Set<string>
  /** Observed interaction counts (see `buildEngagementIndex`). */
  engagement?: EngagementIndex
  /** Overrides `now` for deterministic tests. */
  now?: number
  /** Skips the engagement thresholds while the index is still loading. */
  skipEngagement?: boolean
}

export function defaultPostFilters(): PostFilterState {
  return {
    postTypes: [],
    media: [],
    topics: [],
    topicMode: "include",
    language: "all",
    hideMutedWords: true,
    timeRange: "all",
    customRange: {},
    minLikes: 0,
    minReposts: 0,
    minReplies: 0,
    minTotalEngagement: 0,
    sort: "latest",
    authors: [],
    authorMode: "include",
    following: "everyone",
    relays: [],
  }
}

function nonNegativeInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : fallback
}

function customRangeOf(value: unknown): CustomRange {
  if (!value || typeof value !== "object") return {}
  const raw = value as Record<string, unknown>
  const range: CustomRange = {}
  if (typeof raw.start === "number" && Number.isFinite(raw.start)) range.start = Math.floor(raw.start)
  if (typeof raw.end === "number" && Number.isFinite(raw.end)) range.end = Math.floor(raw.end)
  return range
}

/**
 * Turns anything (persisted JSON, a partial patch, `undefined`) into a valid
 * state. Unknown or malformed fields fall back to their default, so a corrupt
 * localStorage entry can never break a page.
 */
export function normalizePostFilters(value: unknown): PostFilterState {
  const defaults = defaultPostFilters()
  if (!value || typeof value !== "object") return defaults
  const raw = value as Record<string, unknown>

  const postTypes = Array.isArray(raw.postTypes)
    ? raw.postTypes.filter((item) => POST_TYPES.includes(item as PostType))
    : []
  const media = Array.isArray(raw.media)
    ? raw.media.filter((item) => MEDIA_FILTERS.includes(item as MediaFilter))
    : []
  // Topic ids are validated in the lexicon's order so the persisted state is
  // stable and unknown ids from an older build disappear cleanly.
  const selectedTopics = new Set(
    Array.isArray(raw.topics) ? raw.topics.filter((item) => isTopicId(item)) : [],
  )
  const topics = TOPICS.filter((id) => selectedTopics.has(id))
  const languageCodes = new Set(LANGUAGE_OPTIONS.map((option) => option.code))
  const language =
    typeof raw.language === "string" &&
    (raw.language === "all" || raw.language === "detected" || languageCodes.has(raw.language))
      ? raw.language
      : "all"

  return {
    postTypes: [...new Set(postTypes as PostType[])],
    media: [...new Set(media as MediaFilter[])],
    topics,
    topicMode: TOPIC_MODES.includes(raw.topicMode as TopicMode)
      ? (raw.topicMode as TopicMode)
      : "include",
    language,
    hideMutedWords: raw.hideMutedWords !== false,
    timeRange: TIME_RANGES.includes(raw.timeRange as TimeRange) ? (raw.timeRange as TimeRange) : "all",
    customRange: customRangeOf(raw.customRange),
    minLikes: nonNegativeInteger(raw.minLikes, 0),
    minReposts: nonNegativeInteger(raw.minReposts, 0),
    minReplies: nonNegativeInteger(raw.minReplies, 0),
    minTotalEngagement: nonNegativeInteger(raw.minTotalEngagement, 0),
    sort: SORT_MODES.includes(raw.sort as SortMode) ? (raw.sort as SortMode) : "latest",
    authors: Array.isArray(raw.authors)
      ? uniquePubkeys(raw.authors.filter((item): item is string => typeof item === "string"))
      : [],
    authorMode: AUTHOR_MODES.includes(raw.authorMode as AuthorMode)
      ? (raw.authorMode as AuthorMode)
      : "include",
    following: FOLLOWING_MODES.includes(raw.following as FollowingMode)
      ? (raw.following as FollowingMode)
      : "everyone",
    relays: Array.isArray(raw.relays)
      ? [...new Set(raw.relays.filter((item): item is string => typeof item === "string"))]
      : [],
  }
}

/** The single-select post type control as a selection (`"all" | PostType`). */
export type PostTypeSelection = "all" | PostType

export function postTypeSelection(state: PostFilterState): PostTypeSelection {
  return state.postTypes.length === 1 ? state.postTypes[0] : "all"
}

export function withPostTypeSelection(
  state: PostFilterState,
  selection: PostTypeSelection,
): PostFilterState {
  return { ...state, postTypes: selection === "all" ? [] : [selection] }
}

function mutedWordList(words: readonly string[] | undefined): string[] {
  if (!words || words.length === 0) return []
  return words.map((word) => word.trim().toLowerCase()).filter(Boolean)
}

function containsMutedWord(event: NostrEvent, words: readonly string[]): boolean {
  const haystack = event.content.toLowerCase()
  return words.some((word) => haystack.includes(word))
}

/** Everything the engine can check without the engagement index. */
function matchesStaticFilters(event: NostrEvent, state: PostFilterState, context: FilterContext): boolean {
  const now = context.now ?? nowSeconds()
  if (!matchesPostTypes(event, state.postTypes)) return false
  if (!matchesMediaFilters(event, state.media)) return false
  // Topics are always client side: a relay `#t` filter would silently drop
  // untagged posts of the same subject.
  if (!matchesTopicFilter(event, state.topics, state.topicMode)) return false
  if (!matchesLanguageFilter(event, state.language)) return false
  if (!matchesTimeRange(event, state.timeRange, state.customRange, now)) return false
  if (!matchesAuthors(event, state)) return false
  if (!matchesFollowing(event, state.following, context.followingSet)) return false
  if (state.hideMutedWords) {
    const words = mutedWordList(context.mutedWords)
    if (words.length > 0 && containsMutedWord(event, words)) return false
  }
  return true
}

/**
 * Applies every filter except sorting.
 *
 * `context.skipEngagement` is used while the interaction index is still on its
 * way, so a threshold never blanks the feed for a moment.
 */
export function filterPosts(
  events: readonly NostrEvent[],
  state: PostFilterState,
  context: FilterContext = {},
): NostrEvent[] {
  const checkEngagement = !context.skipEngagement && hasEngagementThreshold(state)
  return events.filter((event) => {
    if (!matchesStaticFilters(event, state, context)) return false
    if (
      checkEngagement &&
      !matchesEngagement(event.id, context.engagement, state)
    ) {
      return false
    }
    return true
  })
}

/**
 * Sorting is kept out of the filtering pass: it reorders the same array without
 * changing which posts are visible, so it composes with pagination.
 *
 * Engagement sorts fall back to `created_at` as the tie breaker and are honest
 * about incomplete data: posts whose interactions were not delivered simply rank
 * lower.
 */
export function sortPosts(
  events: readonly NostrEvent[],
  mode: SortMode = "latest",
  engagement?: EngagementIndex,
): NostrEvent[] {
  if (mode === "latest") return sortEvents([...events])
  if (mode === "oldest") {
    return [...events].sort(
      (a, b) => a.created_at - b.created_at || a.id.localeCompare(b.id),
    )
  }
  const score = (event: NostrEvent): number => {
    const counts = engagementOf(engagement, event.id)
    if (mode === "likes") return counts.likes
    if (mode === "reposts") return counts.reposts
    if (mode === "replies") return counts.replies
    return counts.total
  }
  return [...events].sort((a, b) => score(b) - score(a) || b.created_at - a.created_at)
}

/** Filtering + sorting in one call, which is what every page needs. */
export function applyPostFilters(
  events: readonly NostrEvent[],
  state: PostFilterState,
  context: FilterContext = {},
): NostrEvent[] {
  return sortPosts(filterPosts(events, state, context), state.sort, context.engagement)
}

/** One removable chip in the active filter bar. */
export interface ActiveFilter {
  /** Stable id used by `removeFilter` (e.g. `media:image`, `minLikes:5`). */
  key: string
  category: FilterCategory
  /** English label, translated by the UI (`t(label, vars)`). */
  label: string
  vars?: Record<string, string | number>
}

const POST_TYPE_LABELS: Record<PostType, string> = {
  note: "Notes",
  reply: "Replies",
  repost: "Reposts",
  article: "Articles",
}

const MEDIA_LABELS: Record<MediaFilter, string> = {
  text: "Text only",
  image: "Images",
  video: "Videos",
  audio: "Audio",
  link: "Links",
  media: "With media",
}

const TIME_LABELS: Record<Exclude<TimeRange, "all" | "custom">, string> = {
  hour: "Last hour",
  day: "Last 24 hours",
  week: "Last 7 days",
  month: "Last 30 days",
}

/** Every selection that is currently narrowing a list, in panel order. */
export function activeFilters(state: PostFilterState): ActiveFilter[] {
  const filters: ActiveFilter[] = []

  for (const type of state.postTypes) {
    filters.push({ key: `postType:${type}`, category: "content", label: POST_TYPE_LABELS[type] })
  }
  for (const media of state.media) {
    filters.push({ key: `media:${media}`, category: "content", label: MEDIA_LABELS[media] })
  }
  for (const topic of state.topics) {
    filters.push({
      key: `topic:${topic}`,
      category: "content",
      label: state.topicMode === "exclude" ? "Hidden topic: {topic}" : "Topic: {topic}",
      vars: { topic: TOPIC_LABELS[topic] },
    })
  }
  if (state.language !== "all") {
    filters.push({
      key: `language:${state.language}`,
      category: "content",
      label: state.language === "detected" ? "Detected language only" : "Language: {language}",
      vars: state.language === "detected" ? undefined : { language: languageLabel(state.language) },
    })
  }
  if (!state.hideMutedWords) {
    filters.push({ key: "mutedWords:off", category: "content", label: "Muted words ignored" })
  }

  if (state.timeRange === "custom") {
    filters.push({ key: "time:custom", category: "time", label: "Custom date range" })
  } else if (state.timeRange !== "all") {
    filters.push({
      key: `time:${state.timeRange}`,
      category: "time",
      label: TIME_LABELS[state.timeRange],
    })
  }
  if (state.minLikes > 0) {
    filters.push({
      key: `minLikes:${state.minLikes}`,
      category: "time",
      label: "At least {n} likes",
      vars: { n: state.minLikes },
    })
  }
  if (state.minReposts > 0) {
    filters.push({
      key: `minReposts:${state.minReposts}`,
      category: "time",
      label: "At least {n} reposts",
      vars: { n: state.minReposts },
    })
  }
  if (state.minReplies > 0) {
    filters.push({
      key: `minReplies:${state.minReplies}`,
      category: "time",
      label: "At least {n} replies",
      vars: { n: state.minReplies },
    })
  }
  if (state.minTotalEngagement > 0) {
    filters.push({
      key: `minTotalEngagement:${state.minTotalEngagement}`,
      category: "time",
      label: "At least {n} interactions",
      vars: { n: state.minTotalEngagement },
    })
  }

  for (const author of state.authors) {
    filters.push({
      key: `author:${author}`,
      category: "people",
      label: state.authorMode === "exclude" ? "Hidden author: {author}" : "Author: {author}",
      vars: { author: shortNpub(author) },
    })
  }
  if (state.following !== "everyone") {
    filters.push({
      key: `following:${state.following}`,
      category: "people",
      label: state.following === "following" ? "Following only" : "Not following",
    })
  }
  for (const relay of state.relays) {
    filters.push({ key: `source:${relay}`, category: "people", label: simplifyUrl(relay) })
  }

  return filters
}

export function activeFilterCount(state: PostFilterState): number {
  return activeFilters(state).length
}

/** True when nothing is filtered (sorting is not a filter). */
export function isEmptyPostFilters(state: PostFilterState): boolean {
  return activeFilters(state).length === 0
}

/** Removes one selection; unknown keys are ignored so a stale chip is harmless. */
export function removeFilter(state: PostFilterState, key: string): PostFilterState {
  const separator = key.indexOf(":")
  if (separator < 0) return state
  const kind = key.slice(0, separator)
  const value = key.slice(separator + 1)

  switch (kind) {
    case "postType":
      return { ...state, postTypes: state.postTypes.filter((type) => type !== value) }
    case "media":
      return { ...state, media: state.media.filter((media) => media !== value) }
    case "topic":
      return {
        ...state,
        topics: state.topics.filter((topic) => topic !== value),
      }
    case "language":
      return { ...state, language: "all" }
    case "mutedWords":
      return { ...state, hideMutedWords: true }
    case "time":
      return { ...state, timeRange: "all", customRange: {} }
    case "minLikes":
      return { ...state, minLikes: 0 }
    case "minReposts":
      return { ...state, minReposts: 0 }
    case "minReplies":
      return { ...state, minReplies: 0 }
    case "minTotalEngagement":
      return { ...state, minTotalEngagement: 0 }
    case "author":
      return { ...state, authors: state.authors.filter((author) => author !== value) }
    case "following":
      return { ...state, following: "everyone" }
    case "source":
      return { ...state, relays: state.relays.filter((relay) => relay !== value) }
    default:
      return state
  }
}

/** Resets one panel section, leaving the other two untouched. */
export function resetCategory(state: PostFilterState, category: FilterCategory): PostFilterState {
  if (category === "content") {
    return {
      ...state,
      postTypes: [],
      media: [],
      topics: [],
      topicMode: "include",
      language: "all",
      hideMutedWords: true,
    }
  }
  if (category === "time") {
    return {
      ...state,
      timeRange: "all",
      customRange: {},
      minLikes: 0,
      minReposts: 0,
      minReplies: 0,
      minTotalEngagement: 0,
      sort: "latest",
    }
  }
  return { ...state, authors: [], authorMode: "include", following: "everyone", relays: [] }
}

/** The relay-side part of the selection (everything NIP-01 can express). */
export interface RelayFilterHints {
  kinds?: number[]
  since?: number
  until?: number
  authors?: string[]
}

export function relayFilterHints(
  state: PostFilterState,
  now: number = nowSeconds(),
): RelayFilterHints {
  const bounds = timeBounds(state.timeRange, state.customRange, now)
  const hints: RelayFilterHints = {}
  if (state.postTypes.length > 0) hints.kinds = kindsForPostTypes(state.postTypes)
  if (bounds.since !== undefined) hints.since = bounds.since
  if (bounds.until !== undefined) hints.until = bounds.until
  const authors = relayAuthorsFor(state)
  if (authors) hints.authors = authors
  return hints
}


